<template>
  <v-dialog max-width="350px" v-model="dialog">
    <v-overlay v-if="loading">
      <v-progress-circular indeterminate color="primary"/>
    </v-overlay>
    <v-card>
      <v-card-title/>
      <v-card-text>
        <v-row dense>
          <v-col cols="12">
            <v-autocomplete
                v-model="selected"
                :items="sharedConfigs"
                item-text="group"
                item-value="uid"
                label="Select Shared Config"
                clearable
                return-object
                hide-details
            />
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn @click="cancel" color="blue darken-1" text>Cancel</v-btn>
        <v-btn @click="apply" color="blue darken-1" text>Apply</v-btn>
      </v-card-actions>
    </v-card>
    <template v-slot:activator="{ on }">
      <v-text-field v-model.trim="displayName" label="Shared Config" placeholder="None" persistent-placeholder v-on="on" readonly hide-details/>
    </template>
  </v-dialog>
</template>

<script>
import {RedactorUtils} from "@/modules/redactor/core/RedactorUtils";
import {ConfigModel} from "@/modules/mconfigs/models/ConfigModel";
import {deserialize} from "serializr";
import firebase from "firebase";

export default {
  name: "r-shared-config-view",
  data: () => ({
    dialog: false,
    loading: false,
    selected: null,
    sharedConfigs: [],
    currentSharedConfigUid: null,
    displayName: ""
  }),
  methods: {
    apply() {
      let selectedUid = this.selected ? this.selected.uid : null;

      if (this.currentSharedConfigUid !== selectedUid) {
        this.loading = true;
        this.saveSharedConfig(selectedUid).then(() => {
          this.currentSharedConfigUid = selectedUid;
          this.updateDisplayName();
        }).catch(() => {
          // save was aborted or failed — don't update UI
        }).finally(() => {
          this.loading = false;
          this.discard();
        });
      } else {
        this.discard();
      }
    },
    cancel() {
      this.discard();
    },
    discard() {
      this.dialog = false;
    },
    saveSharedConfig(sharedUid) {
      let configUid = RedactorUtils.getConfigUID();

      return this.getConfigMetadataPath(configUid).then((path) => {
        if (!path) return Promise.reject("Config metadata path not found");

        if (path.startsWith("/shared_configs/")) {
          this.$notify({title: 'Cannot set shared config on a shared config', type: "error"});
          return Promise.reject("Cannot set shared config on a shared config");
        }

        return firebase.database()
            .ref(path + "/sharedConfig")
            .set(sharedUid)
            .then(() => {
              return this.$store.dispatch("redactor-store/load");
            })
            .then(() => {
              this.$notify({title: 'Shared config updated!', type: "success"});
            })
            .catch((error) => {
              this.$notify({title: 'Failed to update shared config', text: error, type: "error"});
              return Promise.reject(error);
            });
      });
    },
    getConfigMetadataPath(uid) {
      return firebase.database().ref("/release/" + uid).once("value").then(snap => {
        if (snap.exists()) return "/release/" + uid;
        return firebase.database().ref("/configs/" + uid).once("value").then(snap2 => {
          if (snap2.exists()) return "/configs/" + uid;
          return null;
        });
      });
    },
    fetchSharedConfigsList() {
      firebase.database().ref("/shared_configs").once("value")
          .then((snapshot) => {
            let data = Object.values(snapshot.val() || {});
            this.sharedConfigs = data.map(v => deserialize(ConfigModel, v));
            this.preselectCurrent();
          })
          .catch(() => {
            this.sharedConfigs = [];
          });
    },
    fetchCurrentSharedConfigUid() {
      let configUid = RedactorUtils.getConfigUID();
      if (!configUid) return;

      firebase.database().ref("/release/" + configUid + "/sharedConfig").once("value")
          .then((snap) => {
            if (snap.exists() && snap.val()) {
              this.currentSharedConfigUid = snap.val();
              this.updateDisplayName();
              return;
            }
            return firebase.database().ref("/configs/" + configUid + "/sharedConfig").once("value");
          })
          .then((snap) => {
            if (!snap) return;
            if (snap.exists() && snap.val()) {
              this.currentSharedConfigUid = snap.val();
            }
            this.updateDisplayName();
          })
          .catch(() => {});
    },
    updateDisplayName() {
      if (!this.currentSharedConfigUid) {
        this.displayName = "";
        return;
      }
      firebase.database().ref("/shared_configs/" + this.currentSharedConfigUid + "/group").once("value")
          .then((snap) => {
            this.displayName = snap.exists() ? snap.val() : this.currentSharedConfigUid;
          })
          .catch(() => {
            this.displayName = this.currentSharedConfigUid;
          });
    },
    preselectCurrent() {
      let uid = this.currentSharedConfigUid;
      this.selected = uid
          ? (this.sharedConfigs.find(c => c.uid === uid) || null)
          : null;
    }
  },
  watch: {
    dialog(value) {
      if (value) {
        this.fetchSharedConfigsList();
      }
    },
    currentSharedConfigUid() {
      if (this.dialog) {
        this.preselectCurrent();
      }
    }
  },
  created() {
    this.fetchCurrentSharedConfigUid();
  }
}
</script>
